import { PropsWithChildren, useState } from "react";
import Logo from "../assets/logo.png";
import { useSession } from "../hooks/useSession";
import { IconButton, Tooltip } from "@mui/material";
import {
  Close,
  ExitToApp,
  HelpOutline,
  InfoOutlined,
  Menu,
  SmartToy,
} from "@mui/icons-material";
import { Link, NavLink, useNavigate } from "react-router-dom";
import clsx from "clsx";
import Loading from "../components/Loading";

const ASSISTANT_URL = "https://chatgpt.com/g/g-KLLRcq3Zj-qualifica-urb";
const TUTORIAL_URL =
  "https://drive.google.com/file/d/1Cg4rBwMLi8SFjiMEaalvSlyfeeJlnqdL/view";

const navItems = [
  { to: "/home", label: "Início" },
  { to: "/avaliacoes", label: "Avaliações" },
  { to: "/avaliacoes/criar", label: "Nova avaliação" },
];

export default function Layout(props: PropsWithChildren) {
  const { children } = props;
  const navigate = useNavigate();
  const { profile, logout } = useSession();
  const [menuOpen, setMenuOpen] = useState(false);

  if (!profile) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface">
        <Loading label="Carregando perfil…" />
      </div>
    );
  }

  const firstName = profile.name?.split(" ")[0] || profile.name;

  const navClass = ({ isActive }: { isActive: boolean }) =>
    clsx(
      "rounded-lg px-3 py-2 text-sm font-semibold transition-colors",
      isActive
        ? "bg-white/20 text-white"
        : "text-white/85 hover:bg-white/10 hover:text-white"
    );

  const openTutorial = () => window.open(TUTORIAL_URL, "_blank");
  const openAssistant = () => window.open(ASSISTANT_URL, "_blank");

  return (
    <div className="relative min-h-screen bg-surface">
      <a className="skip-link" href="#conteudo">
        Ir para o conteúdo
      </a>

      <header className="sticky top-0 z-40 bg-primary shadow-header">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <Link
            className="flex min-w-0 items-center gap-3 text-white"
            to="/home"
          >
            <img
              src={Logo}
              alt=""
              width={48}
              height={48}
              className="h-10 w-10 shrink-0 object-contain sm:h-12 sm:w-12"
            />
            <span className="min-w-0">
              <span className="block truncate text-lg font-extrabold tracking-tight sm:text-xl">
                QualificaURB
              </span>
            </span>
          </Link>

          <nav
            className="hidden items-center gap-1 lg:flex"
            aria-label="Principal"
          >
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={navClass}
                end={item.to === "/avaliacoes"}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center">
            <p className="mr-1 hidden truncate text-sm text-white/85 lg:block">
              Olá, {firstName}
            </p>
            <div className="hidden lg:flex">
              <Tooltip title="Vídeo tutorial">
                <IconButton
                  aria-label="Abrir vídeo tutorial"
                  onClick={openTutorial}
                  sx={{
                    color: "#FFFFFF",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.14)" },
                  }}
                >
                  <HelpOutline />
                </IconButton>
              </Tooltip>
              <Tooltip title="Assistente QualificaURB">
                <IconButton
                  aria-label="Abrir assistente QualificaURB"
                  onClick={openAssistant}
                  sx={{
                    color: "#FFFFFF",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.14)" },
                  }}
                >
                  <SmartToy />
                </IconButton>
              </Tooltip>
              <Tooltip title="Sobre a ferramenta">
                <IconButton
                  aria-label="Abrir créditos"
                  onClick={() => navigate("/creditos")}
                  sx={{
                    color: "#FFFFFF",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.14)" },
                  }}
                >
                  <InfoOutlined />
                </IconButton>
              </Tooltip>
              <Tooltip title="Sair">
                <IconButton
                  aria-label="Sair da conta"
                  onClick={logout}
                  sx={{
                    color: "#FFFFFF",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.14)" },
                  }}
                >
                  <ExitToApp />
                </IconButton>
              </Tooltip>
            </div>
            <div className="lg:hidden">
              <IconButton
                aria-label={menuOpen ? "Fechar menu" : "Abrir menu"}
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((open) => !open)}
                sx={{
                  color: "#FFFFFF",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.14)" },
                }}
              >
                {menuOpen ? (
                  <Close className="text-white" />
                ) : (
                  <Menu className="text-white" />
                )}
              </IconButton>
            </div>
          </div>
        </div>

        {menuOpen && (
          <nav
            className="flex flex-col gap-1 border-t border-white/15 px-4 py-3 lg:hidden"
            aria-label="Menu móvel"
          >
            <p className="px-3 pb-2 text-sm text-white/80">Olá, {firstName}</p>
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={navClass}
                end={item.to === "/avaliacoes"}
                onClick={() => setMenuOpen(false)}
              >
                {item.label}
              </NavLink>
            ))}
            <button
              type="button"
              className="rounded-lg px-3 py-2 text-left text-sm font-semibold text-white/85 hover:bg-white/10"
              onClick={() => {
                openTutorial();
                setMenuOpen(false);
              }}
            >
              Vídeo tutorial
            </button>
            <button
              type="button"
              className="rounded-lg px-3 py-2 text-left text-sm font-semibold text-white/85 hover:bg-white/10"
              onClick={() => {
                openAssistant();
                setMenuOpen(false);
              }}
            >
              Assistente
            </button>
            <button
              type="button"
              className="rounded-lg px-3 py-2 text-left text-sm font-semibold text-white/85 hover:bg-white/10"
              onClick={() => {
                navigate("/creditos");
                setMenuOpen(false);
              }}
            >
              Sobre a ferramenta
            </button>
            <button
              type="button"
              className="rounded-lg px-3 py-2 text-left text-sm font-semibold text-white/85 hover:bg-white/10"
              onClick={logout}
            >
              Sair
            </button>
          </nav>
        )}
      </header>

      <main
        id="conteudo"
        className="mx-auto max-w-7xl scroll-mt-24 px-4 py-8 sm:px-6"
      >
        {children}
      </main>
    </div>
  );
}
